/* prettier-ignore */

import { INFORMER_MODE_QUERY_PARAM, INFORMER_REGISTER_QUERY_PARAM, InformerMode, toInformerMode, toInformerRegisterId } from '../interfaces/informer-mode.interface';

/**
 * Helpers puros (sin DI, testeables) de la entrada al modo del informador por
 * ruta de navegación (HRS-3145).
 *
 * Hasta ahora se entraba con `?techMode=true` (histórico) o `?informer=<modo>`
 * (HRS-2990), y `AppComponent` consumía el param y lo borraba de la URL: no se
 * veía dónde estabas ni se podía editar a mano. Ahora la entrada es una ruta,
 * `/tech-mode/...`, que hace de **prefijo de entrada**: fija el modo, se quita
 * a sí misma y redirige al destino. No es un prefijo permanente (la web sigue
 * navegando por `/home`, `/custom-route`…), así que el modo sigue viviendo en
 * localStorage como hasta ahora.
 *
 * Formas admitidas:
 * - `/tech-mode`                       → `predefined-route`, destino `/home`.
 * - `/tech-mode/<ruta interna>`        → `predefined-route`, destino `/<ruta interna>`
 *                                        (p. ej. `register-user/{token}`, HRS-3137).
 * - `/tech-mode/complete[/<ruta>]`     → `complete`; el registro sigue en `?register={id}`.
 *
 * El modo `simple` no tiene forma aquí: ya tiene rutas propias
 * (`tech-register-by-id/:id`, `tech-register-by-token/:token`).
 *
 * Los query params heredados se mantienen (hay QR impresos con `?techMode=true`)
 * y **redirigen** a la ruta, ver `legacyInformerRedirect`.
 */

export const TECH_MODE_PATH = 'tech-mode';

/** Segmento que, justo tras el prefijo, cambia el modo a `complete`. */
const COMPLETE_SEGMENT = 'complete';

/** Alias histórico de `?informer=predefined-route`. */
const LEGACY_TECH_MODE_QUERY_PARAM = 'techMode';

/** Params que identifican la entrada y no deben llegar al destino. */
const CONSUMED_QUERY_PARAMS = [LEGACY_TECH_MODE_QUERY_PARAM, INFORMER_MODE_QUERY_PARAM, INFORMER_REGISTER_QUERY_PARAM];

export interface InformerModeEntry {
  mode: InformerMode;
  registerId: string | null;
}

export interface TechModeEntry extends InformerModeEntry {
  /** URL interna a la que redirigir: sin el prefijo y sin los params consumidos. */
  target: string;
}

export interface LegacyInformerRedirect {
  /**
   * Modo que hay que activar en el propio guard porque no tiene ruta (`simple`).
   * `null` cuando la activación la hará `/tech-mode/...`, que es el destino.
   */
  enter: InformerModeEntry | null;
  target: string;
}

interface SplitUrl {
  segments: string[];
  query: URLSearchParams;
  hash: string;
}

/** Parte una URL interna del router (`/a/b?x=1#h`) en segmentos, query y hash. */
function splitUrl(url: string): SplitUrl {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;

  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const query = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '');

  return { segments: path.split('/').filter(Boolean), query, hash };
}

function joinUrl(segments: string[], query: URLSearchParams, hash: string): string {
  const path = '/' + segments.join('/');
  const queryString = query.toString();
  return path + (queryString ? `?${queryString}` : '') + hash;
}

/**
 * Resuelve una URL `/tech-mode/...`. Devuelve `null` si la URL no empieza por
 * el prefijo (no debería pasar: el guard solo se monta bajo esa ruta).
 */
export function resolveTechModeEntry(url: string): TechModeEntry | null {
  const { segments, query, hash } = splitUrl(url);
  if (segments[0] !== TECH_MODE_PATH) return null;

  const rest = segments.slice(1);
  let mode: InformerMode = 'predefined-route';
  if (rest[0] === COMPLETE_SEGMENT) {
    mode = 'complete';
    rest.shift();
  }

  const registerId = toInformerRegisterId(query.get(INFORMER_REGISTER_QUERY_PARAM));
  CONSUMED_QUERY_PARAMS.forEach((param) => query.delete(param));

  return {
    mode,
    registerId,
    target: joinUrl(rest.length ? rest : ['home'], query, hash),
  };
}

/**
 * Traduce la entrada por query param heredada a la ruta nueva.
 *
 * - `?techMode=<lo que sea>` → `predefined-route` (misma semántica que antes:
 *   cualquier valor no vacío). `?informer=<modo>` manda si viene válido.
 * - `predefined-route` y `complete` → redirige a `/tech-mode[/complete]<misma ruta>`
 *   conservando el resto de params (`token`, `lang`, `register`…): los consume
 *   quien los consumía, `initializeParams` y el guard de `/tech-mode`.
 * - `simple` no tiene ruta: se activa aquí (`enter`) y se redirige a la misma
 *   ruta sin los params, que es lo que antes hacía `cleanConsumedQueryParams`.
 * - Sin params de entrada, o con `?informer=` inválido → `null`, no se redirige.
 * - Si la URL ya está bajo `/tech-mode`, solo se quitan los params de modo (el
 *   `register` se deja para que lo consuma el guard del prefijo).
 */
export function legacyInformerRedirect(url: string): LegacyInformerRedirect | null {
  const { segments, query, hash } = splitUrl(url);

  const mode = toInformerMode(query.get(INFORMER_MODE_QUERY_PARAM))
    ?? (query.get(LEGACY_TECH_MODE_QUERY_PARAM) ? 'predefined-route' : null);
  if (!mode) return null;

  query.delete(LEGACY_TECH_MODE_QUERY_PARAM);
  query.delete(INFORMER_MODE_QUERY_PARAM);

  if (segments[0] === TECH_MODE_PATH)
    return { enter: null, target: joinUrl(segments, query, hash) };

  if (mode === 'simple') {
    const registerId = toInformerRegisterId(query.get(INFORMER_REGISTER_QUERY_PARAM));
    query.delete(INFORMER_REGISTER_QUERY_PARAM);
    return { enter: { mode, registerId }, target: joinUrl(segments, query, hash) };
  }

  const prefix = mode === 'complete' ? [TECH_MODE_PATH, COMPLETE_SEGMENT] : [TECH_MODE_PATH];
  return { enter: null, target: joinUrl([...prefix, ...segments], query, hash) };
}

/**
 * URL de otro subdominio (portal del distribuidor ↔ web del municipio) que
 * conserva el modo del informador. El modo vive en el localStorage de cada
 * subdominio, así que al saltar se perdía; con la ruta de entrada basta con
 * añadir el prefijo. `simple` no tiene ruta y `null` es el visitante: en los
 * dos casos se devuelve la raíz.
 *
 * `base` puede venir con o sin barra final (`getDistributorUrl` la trae,
 * `location.origin` no); `search` es el query string ya formado (`?distributor=x`).
 *
 * Si la base trae ya el prefijo de entrada se quita: `homeDistributor` se calcula
 * en el `ngOnInit`, cuando la URL todavía es `/tech-mode/...`, y `getDistributorUrl`
 * conserva esa ruta. Sin esto saldría `/tech-mode/tech-mode/` y, tras salir del
 * modo, el botón de volver al portal volvería a activarlo.
 */
export function crossSiteUrl(base: string, mode: InformerMode | null, search = ''): string {
  const origin = base
    .replace(/\/+$/, '')
    .replace(new RegExp(`/${TECH_MODE_PATH}(/${COMPLETE_SEGMENT})?$`), '');
  const entry = mode === 'predefined-route' ? `/${TECH_MODE_PATH}/`
    : mode === 'complete' ? `/${TECH_MODE_PATH}/${COMPLETE_SEGMENT}/`
    : '/';
  return origin + entry + search;
}
